import React, { useRef } from 'react';
import Checkbox from '../../../commons/component/checkbox';
import Error from '../../../commons/component/error';
import Input from '../../../commons/component/input';
import { InputPhone } from '../../../commons/component/input-mask';
import PasswordScore from '../../password-score';
import EyeIcon from '../../../icons/eye-svg';
import AempUserIcon from '../../../icons/aemp-user-icon';
import { useSignUpFormContext } from '../../cadastro-context';
import Button from '../../../commons/component/button';
import config from '../../../generated/config';
import {
  checkBuyerEmailField,
  checkBuyerPhoneField,
  checkNameField,
  checkPasswordField,
  dispatchMetricOnBuyerFormSubmit,
  hasError,
  hasPatternError
} from '../../cadastro-services';

const CadastroComprador = ({ history }) => {
  const { updateState, ...cadastroState } = useSignUpFormContext();
  const { errors } = cadastroState;
  const buyerNameRef = useRef();
  const buyerPhoneRef = useRef();
  const buyerEmailRef = useRef();
  const buyerPasswordRef = useRef();
  const inputRefMap = {
    buyerName: buyerNameRef,
    buyerPhone: buyerPhoneRef,
    buyerEmail: buyerEmailRef,
    buyerPassword: buyerPasswordRef
  };

  return (
    <div className="cadastroForm-container">
      <h2 className="cadastroForm-title">
        <AempUserIcon />
        <span>{config.signUpScreen.buyer.formTitle}</span>
      </h2>
      <div className="cadastroComprador">
        <div className="compradorHeader">
          <p className="compradorSubtitle">
            <span className="required-asterisk">*</span> {config.signUpScreen.buyer.requiredFields}
          </p>
        </div>

        <form
          className="cadastroForm"
          onSubmit={e => {
            e.preventDefault();
            if (hasPatternError(errors)) {
              const errorArray = Object.keys(errors);
              const error = errorArray[0];
              if (inputRefMap[error]) {
                inputRefMap[error].current.focus();
                return;
              }
            }
            dispatchMetricOnBuyerFormSubmit();
            history.push('/cadastro/empresa');
            return;
          }}
        >
          <div className="inputGroup" id="buyerName">
            <label className="inputGroup-label" htmlFor="buyerName-input">
              <span className="required-asterisk">*</span> seu nome completo
            </label>
            <Input
              name="buyerName"
              id="buyerName-input"
              onChange={e =>
                updateState({
                  ...cadastroState,
                  buyer: { ...cadastroState.buyer, buyerName: e.target.value }
                })
              }
              onBlur={e => checkNameField(e.target.value, 'buyerName', cadastroState, updateState)}
              value={cadastroState.buyer.buyerName}
              data-page="signup"
              data-form="signup"
              className={`inputGroup-input --large ${hasError('buyerName', errors)}`}
              type="text"
              inputRef={buyerNameRef}
              required
            />
            <Error errors={errors} name="buyerName" />
          </div>

          <div className="inputGroup" id="buyerPhone">
            <label className="inputGroup-label" htmlFor="buyerPhone-input">
              <span className="required-asterisk">*</span> celular
            </label>
            <InputPhone
              data-form="signup"
              data-page="signup"
              onChange={e =>
                updateState({
                  ...cadastroState,
                  buyer: { ...cadastroState.buyer, buyerPhone: e.target.value }
                })
              }
              onBlur={e =>
                checkBuyerPhoneField(e.target.value, 'buyerPhone', cadastroState, updateState)
              }
              value={cadastroState.buyer.buyerPhone}
              name="buyerPhone"
              id="buyerPhone-input"
              className={`inputGroup-input ${hasError('buyerPhone', errors)}`}
              type="text"
              placeholder="Ex: (99) 99999-9999"
              inputRef={buyerPhoneRef}
              required
            />
            <Error errors={errors} name="buyerPhone" />
          </div>

          <div className="inputGroup" id="buyerEmail">
            <label className="inputGroup-label" htmlFor="buyerEmail-input">
              <span className="required-asterisk">*</span> e-mail
            </label>
            <Input
              placeholder="Ex. joaodasilva@gmail.com"
              className={`inputGroup-input --large ${hasError('buyerEmail', errors)} ${hasError(
                'buyerAlreadyRegistered',
                errors
              )}`}
              data-page="signup"
              data-form="signup"
              name="buyerEmail"
              onChange={e =>
                updateState({
                  ...cadastroState,
                  buyer: { ...cadastroState.buyer, buyerEmail: e.target.value }
                })
              }
              onBlur={e => checkBuyerEmailField(e.target.value, cadastroState, updateState)}
              value={cadastroState.buyer.buyerEmail}
              id="buyerEmail-input"
              type="email"
              inputRef={buyerEmailRef}
              required
            />
            <Error errors={errors} name="buyerEmail" />
            <Error errors={errors} name="buyerAlreadyRegistered" />
          </div>

          <div className="inputGroup" id="buyerPassword">
            <label className="inputGroup-label" htmlFor="buyerPassword-input">
              <span className="required-asterisk">*</span> senha
              <span className="passwordHelp">{config.signUpScreen.buyer.passwordHelp}</span>
            </label>
            <div className="passwordWrapper">
              <Input
                name="buyerPassword"
                id="buyerPassword-input"
                data-form="signup"
                data-page="signup"
                type={cadastroState.showPassword ? 'text' : 'password'}
                onChange={e =>
                  updateState({
                    ...cadastroState,
                    passwordScore: window.zxcvbn && window.zxcvbn(e.target.value || '').score,
                    buyer: {
                      ...cadastroState.buyer,
                      buyerPassword: e.target.value
                    }
                  })
                }
                onBlur={e => checkPasswordField(e.target.value, cadastroState, updateState)}
                value={cadastroState.buyer.buyerPassword}
                className={`inputGroup-input --withAddOn ${hasError('buyerPassword', errors)}`}
                inputRef={buyerPasswordRef}
                required
              />
              <div
                role="button"
                className="inputGroup-addOn"
                onClick={() =>
                  updateState({ ...cadastroState, showPassword: !cadastroState.showPassword })
                }
              >
                <EyeIcon on={!cadastroState.showPassword} />
              </div>
            </div>
            <Error errors={errors} name="buyerPassword" />
            {!errors.buyerAlreadyRegistered && (
              <PasswordScore className="inputGroup-score" score={cadastroState.passwordScore} />
            )}
          </div>

          <div className="inputGroup --checkbox">
            <Checkbox
              className="inputGroup-checkbox"
              name="offersEmail"
              onChange={() =>
                updateState({
                  ...cadastroState,
                  buyer: { ...cadastroState.buyer, offersEmail: !cadastroState.buyer.offersEmail }
                })
              }
              checked={cadastroState.buyer.offersEmail}
              data-form="signup"
              data-page="signup"
            >
              receber e-mails promocionais
            </Checkbox>
          </div>
          <div className="cadastro-buttons">
            <Button
              primary
              disabled={cadastroState.isFormSubmitting}
              loading={cadastroState.isFormSubmitting}
              className="cadastroForm-submit"
            >
              {config.signUpScreen.buyer.buttonSubmit}
            </Button>
            <button
              className="btn button-ghost"
              disabled={cadastroState.isFormSubmitting}
              onClick={() => history.push('/')}
            >
              {config.signUpScreen.buyer.goBack}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CadastroComprador;
